import React, { useMemo } from 'react';
import ReactEcharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';
import { traceSegments } from '../../../depthTrace';
import { timeAxisZoom, timeChartToolbox, ZOOMED_GRID_BOTTOM } from './timeZoom';

// The depth counter's run: wireline depth against time, with the survey's shots
// laid over it.
//
// The shots sitting on the curve is most of the point. They are two independent
// records of the same run — the operator's marks and the counter's own stream —
// so seeing them agree is the evidence, and it is why this replaces the plain
// depth-vs-time chart rather than sitting beside it.
//
// The line is deliberately broken in two different circumstances and they do not
// mean the same thing, so the chart has to say which:
//
//   * a hole WITH a diamond is a break — the operator zeroed the counter, typed a
//     new datum, inverted it or reconnected. The depth step across it is
//     bookkeeping, not movement.
//   * a hole WITHOUT one is a dropped link. A stationary tool still leaves a
//     trail (the thinner emits a point every 30 s with nothing else to say), so a
//     hole means the counter stopped reporting, not that the tool stopped.
//
// Joining across either would draw movement that never happened.
const COLOURS = {
    trace: '#5470c6',
    break: '#fac858',
    mark: '#91cc75',
    uncovered: '#ee6666'
};

// What each uncovered mark is told about itself. None of these is a failure:
// shots taken before the counter was connected have no trace to lie on, and that
// is normal.
const REASONS = {
    'before-trace': 'depthRun.reasonBeforeTrace',
    'after-trace': 'depthRun.reasonAfterTrace',
    'in-gap': 'depthRun.reasonInGap',
    'across-break': 'depthRun.reasonAcrossBreak',
    'no-trace': 'depthRun.reasonNoTrace'
};

export const DepthTraceChart = ({ trace, coverage, markLabel, height = 300 }) => {
    const { t } = useTranslation();
    // Memoised so a parent re-render does not re-apply the option and throw away
    // the user's zoom (see timeZoom.js). DepthRunPanel rebuilds `coverage` on
    // every render, so the marks are keyed by content rather than by identity.
    const marksKey = JSON.stringify(coverage?.marks || []);
    const option = useMemo(() => {
        const segments = traceSegments(trace);
        if (segments.length === 0) return null;

        const label = markLabel ?? t('depthRun.shot');
        const marks = coverage?.marks || [];
        const covered = marks.filter(mark => mark.status === 'covered');
        const uncovered = marks.filter(mark => mark.status !== 'covered');

        // One line series for the whole run, with a null between segments. Echarts
        // leaves a null unjoined (connectNulls defaults false), which is exactly the
        // behaviour wanted — and it keeps the series count constant at four however
        // many segments a survey happens to have. A series per segment would make the
        // count vary with the data, which is the palette-repaint and stranded-series
        // hazard the other charts document and chartSeries.test.js guards.
        //
        // A single-point segment therefore contributes a point and then a null, and
        // draws nothing. Its diamond below is what makes it visible, which is right:
        // zeroing the counter is an instant, not a run.
        const line = [];
        segments.forEach((segment, index) => {
            if (index > 0) line.push([segment.from.toISOString(), null]);
            segment.points.forEach(point => line.push([point.at.toISOString(), point.depth]));
        });

        const breaks = segments
            .filter(segment => segment.startedBy === 'break')
            .map(segment => [segment.from.toISOString(), segment.points[0].depth]);

        // Extra fields ride in the tuple past index 1 so the tooltip can reach them,
        // the same trick SurveyTraceChart uses to carry a station's depth onto a
        // projected point.
        const coveredPoints = covered.map(mark =>
            [mark.at.toISOString(), mark.depth, mark.residual, mark.interpolated]);
        const uncoveredPoints = uncovered.map(mark =>
            [mark.at.toISOString(), mark.depth, t(REASONS[mark.reason] || 'depthRun.reasonUnknown')]);

        // The markup stays here rather than in the translations, so a translator can
        // only ever change words.
        return {
            title: {
                text: t('depthRun.chartTitle'),
                left: 'center'
            },
            // Per-item rather than per-axis: the marks are the payload here, and each
            // one carries its own cross-check result to report.
            tooltip: {
                trigger: 'item'
            },
            toolbox: timeChartToolbox,
            dataZoom: timeAxisZoom(),
            // The legend sits under the title, because the zoom slider needs the bottom edge.
            legend: {
                top: 30
            },
            grid: {
                top: 70,
                bottom: ZOOMED_GRID_BOTTOM
            },
            xAxis: {
                type: 'time',
                scale: true,
                splitLine: { show: true }
            },
            yAxis: {
                name: t('depthRun.depthAxis'),
                type: 'value',
                // No min: the counter reads signed and crosses zero (the one real run
                // reaches -0.1 m above its datum). Not inverted either, so this reads
                // the same way round as the plain depth-vs-time chart it stands in for.
                scale: true,
                axisLabel: { formatter: '{value}' },
                splitLine: { show: true }
            },
            series: [
                {
                    name: t('depthRun.counterSeries'),
                    type: 'line',
                    showSymbol: false,
                    smooth: false,
                    itemStyle: { color: COLOURS.trace },
                    lineStyle: { color: COLOURS.trace, width: 1.5 },
                    data: line,
                    z: 2,
                    tooltip: {
                        formatter: (params) =>
                            `${t('depthRun.counter')}<br/>${t('depthRun.depthValue', { depth: metres(params.data[1]) })}`
                    }
                },
                {
                    name: t('depthRun.breakSeries'),
                    type: 'scatter',
                    symbol: 'diamond',
                    symbolSize: 9,
                    itemStyle: { color: COLOURS.break },
                    data: breaks,
                    z: 3,
                    tooltip: {
                        formatter: (params) =>
                            `${t('depthRun.zeroedOrReconnected')}<br/>`
                            + `${t('depthRun.resumesAt', { depth: metres(params.data[1]) })}<br/>`
                            + `<span style="color:#888">${t('depthRun.bookkeeping')}</span>`
                    }
                },
                {
                    name: label,
                    type: 'scatter',
                    symbol: 'circle',
                    symbolSize: 9,
                    itemStyle: { color: COLOURS.mark },
                    data: coveredPoints,
                    z: 4,
                    tooltip: {
                        formatter: (params) =>
                            `${t('depthRun.markAt', { mark: label, depth: metres(params.data[1]) })}<br/>`
                            + `${t('depthRun.counterReads', { depth: metres(params.data[3]) })}<br/>`
                            + t('depthRun.difference', { value: metres(params.data[2]) })
                    }
                },
                {
                    name: t('depthRun.markNoTrace', { mark: label }),
                    type: 'scatter',
                    symbol: 'circle',
                    symbolSize: 9,
                    // Hollow, the same treatment rejected stations get on the trace
                    // chart. It marks "nothing to compare against", not a failure.
                    itemStyle: { color: 'transparent', borderColor: COLOURS.uncovered, borderWidth: 2 },
                    data: uncoveredPoints,
                    z: 4,
                    tooltip: {
                        formatter: (params) =>
                            `${t('depthRun.markAt', { mark: label, depth: metres(params.data[1]) })}<br/>`
                            + t('depthRun.notOnTrace', { reason: params.data[2] })
                    }
                }
            ]
        };
    }, [trace, marksKey, markLabel, t]); // eslint-disable-line react-hooks/exhaustive-deps -- coverage is keyed by marksKey
    if (!option) return null;

    return (
        <ReactEcharts
            option={option}
            // Same reason as the other record charts: replace rather than merge,
            // so a previous survey's series cannot be stranded on the canvas.
            notMerge={true}
            // The slider and the legend moved to the top cost 20 px, added here so the
            // plot area stays the size the callers' heights were chosen for.
            style={{ height: (height + 20) + 'px', width: '100%' }}
        />
    );
};

const metres = (value) => (Number.isFinite(value) ? `${value.toFixed(2)} m` : '-');
